import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, BarChart3, Target, Shield, ToggleLeft } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';

const CookiePolicyPage = () => {
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_preferences', JSON.stringify(preferences));
    alert('Préférences de cookies enregistrées !');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">REVELIAE</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl mb-4">
              <Cookie className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Politique de Cookies</h1>
            <p className="text-gray-500">Dernière mise à jour : 25 février 2026</p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Cookie className="h-6 w-6 text-amber-500" />
                <h2 className="text-xl font-bold text-gray-900">Qu'est-ce qu'un cookie ?</h2>
              </div>
              <p className="text-gray-600">
                Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, tablette, smartphone) 
                lorsque vous visitez un site web. Les cookies permettent au site de mémoriser vos actions et préférences 
                (comme la connexion, la langue, la taille de police et d'autres préférences d'affichage) pendant une 
                période donnée, afin que vous n'ayez pas à les ressaisir à chaque visite.
              </p>
            </section>

            {/* Cookie Types */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <Settings className="h-6 w-6 text-blue-500" />
                <h2 className="text-xl font-bold text-gray-900">Types de cookies utilisés</h2>
              </div>

              {/* Essential Cookies */}
              <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-green-600" />
                    <h3 className="font-bold text-gray-900">Cookies essentiels</h3>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Obligatoires</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-green-600">Toujours actifs</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Ces cookies sont nécessaires au fonctionnement du site. Ils permettent d'utiliser les principales 
                  fonctionnalités comme la navigation et l'accès aux zones sécurisées.
                </p>
                <div className="bg-white rounded-lg p-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="pb-2">Cookie</th>
                        <th className="pb-2">Finalité</th>
                        <th className="pb-2">Durée</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">session_token</td>
                        <td className="py-2">Authentification</td>
                        <td className="py-2">7 jours</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-mono text-xs">cookie_preferences</td>
                        <td className="py-2">Mémoriser vos choix</td>
                        <td className="py-2">1 an</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    <h3 className="font-bold text-gray-900">Cookies analytiques</h3>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Optionnels</span>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      preferences.analytics ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      preferences.analytics ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Ces cookies nous aident à comprendre comment les visiteurs interagissent avec le site en collectant 
                  des informations de manière anonyme.
                </p>
                <div className="bg-white rounded-lg p-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="pb-2">Cookie</th>
                        <th className="pb-2">Finalité</th>
                        <th className="pb-2">Durée</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      <tr>
                        <td className="py-2 font-mono text-xs">_ga</td>
                        <td className="py-2">Google Analytics</td>
                        <td className="py-2">2 ans</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Target className="h-5 w-5 text-purple-600" />
                    <h3 className="font-bold text-gray-900">Cookies marketing</h3>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Optionnels</span>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      preferences.marketing ? 'bg-purple-500' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      preferences.marketing ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
                <p className="text-gray-600 text-sm">
                  Ces cookies sont utilisés pour afficher des publicités pertinentes. Ils peuvent également limiter 
                  le nombre de fois où vous voyez une publicité et aider à mesurer l'efficacité des campagnes.
                </p>
              </div>
            </section>

            {/* Save Preferences */}
            <div className="p-6 bg-gradient-to-r from-gray-100 to-gray-50 rounded-2xl border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Enregistrer vos préférences</h3>
                  <p className="text-sm text-gray-500">Vos choix seront mémorisés pour 1 an</p>
                </div>
                <Button
                  onClick={handleSavePreferences}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                >
                  Enregistrer
                </Button>
              </div>
            </div>

            {/* How to manage */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <ToggleLeft className="h-6 w-6 text-gray-500" />
                <h2 className="text-xl font-bold text-gray-900">Comment gérer les cookies ?</h2>
              </div>
              <p className="text-gray-600 mb-4">
                Vous pouvez également gérer les cookies directement dans les paramètres de votre navigateur :
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/fr/kb/protection-renforcee-contre-pistage-firefox-ordinateur" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Microsoft Edge</a></li>
              </ul>
            </section>

            {/* Contact */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Contact</h2>
              <p className="text-gray-600">
                Pour toute question concernant notre utilisation des cookies :{' '}
                <a href="mailto:privacy@reveliae.com" className="text-blue-600 hover:underline">privacy@reveliae.com</a>
              </p>
            </section>
          </div>

          {/* Back link */}
          <div className="mt-12 text-center">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              ← Retour à l'accueil
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default CookiePolicyPage;
